import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { createPortal } from 'react-dom'

/**
 * A panel over the screen, with the screen still behind it.
 *
 * The board's dialogs are things done *to* what is on the board — clearing its
 * duplicates, adding a row to it — so they are not screens of their own. A
 * route for each is what made them tabs, and leaving the list to add to it and
 * coming back to find it as it was is the shape that produced.
 *
 * Rendered into the document rather than in place, for the same reason
 * `Popover` is: `Table` puts its rows in an `overflow-x-auto` container, and a
 * container that scrolls on one axis clips on both.
 *
 * **Three ways out, because a dialog with one is a trap**: the scrim, Escape,
 * and whatever the caller puts in `children`. The scrim is a button so that
 * closing by clicking away is reachable from a keyboard as well as a mouse.
 *
 * It is deliberately not the platform `<dialog>` element. That brings its own
 * focus trap, its own backdrop and its own open/close state, and the state
 * would then live in two places — the element's and the caller's — which is one
 * more than can be kept true.
 */
export function Dialog({
  heading,
  description,
  footer,
  wide,
  onClose,
  children,
}: {
  /** Names the dialog to a screen reader as well as heading it. */
  readonly heading: string
  /**
   * One line under the heading, where the dialog needs explaining.
   *
   * `| undefined` spelled out because `exactOptionalPropertyTypes` is on: a
   * caller passing a value that may be absent — which is what a conditional
   * expression produces — would otherwise not be assignable.
   */
  readonly description?: string | undefined
  /**
   * The actions, pinned below the content rather than inside it.
   *
   * A form long enough to scroll otherwise scrolls its own Save button out of
   * sight, which is how somebody fills a form in and cannot find the way to
   * submit it.
   */
  readonly footer?: ReactNode
  /**
   * A wider panel, for a dialog that is a sequence rather than a question.
   *
   * The default is the width a confirmation wants. The new-campaign wizard
   * holds a message preview and a row of three cards, and at the default both
   * of those stack into a column tall enough to scroll — which puts the step
   * somebody is on out of sight of the Next button that leaves it.
   */
  readonly wide?: boolean | undefined
  readonly onClose: () => void
  readonly children: ReactNode
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        aria-label="Close"
        className="absolute inset-0 cursor-default bg-t1/30"
        onClick={onClose}
        type="button"
      />

      <div
        aria-labelledby="dialog-heading"
        aria-modal="true"
        className={`relative flex max-h-[calc(100dvh-2rem)] w-full ${wide === true ? 'max-w-3xl' : 'max-w-xl'} flex-col overflow-hidden rounded-card border border-line bg-surface shadow-2xl`}
        role="dialog"
      >
        <header className="flex flex-none items-start justify-between gap-4 border-line border-b px-6 py-4">
          <div>
            <h2 className="font-bold text-[17px] text-t1" id="dialog-heading">
              {heading}
            </h2>
            {description === undefined ? null : (
              <p className="mt-1 text-[13px] text-t2">{description}</p>
            )}
          </div>
          {/* A cross as well as the scrim and Escape: it is the one way out
              that is visible without being tried. */}
          <button
            aria-label="Close"
            className="-mt-1 -mr-2 flex-none rounded-field px-2 py-1 text-[18px] text-t3 leading-none hover:bg-line-2 hover:text-t1"
            onClick={onClose}
            type="button"
          >
            ×
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>

        {footer === undefined ? null : (
          <footer className="flex flex-none flex-wrap items-center gap-3 border-line border-t bg-surface-2 px-6 py-4">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  )
}
